<template>
  <div>
    <!-- This component wraps the table, so the table is its slot content -->
    <slot></slot>
  </div>
</template>

<script setup>
import { ref, onMounted, onUnmounted, watch } from 'vue';
import axios from 'axios';

const props = defineProps({
  initialTokens: {
    type: Array,
    required: true,
  }
});

const prices = ref({}); // Stores { mint: { price_usd: number, change_24h: number, volume_24h: number, ... } }
const previousPrices = ref({}); // Stores previous prices for comparison
const mints = ref([]);
let intervalId = null;

// --- Formatting Helper ---
const formatPrice = (price) => {
    if (price === null || price === undefined || price === 0) return '0.00';
    if (price > 1) return price.toFixed(2);
    if (price > 0.01) return price.toFixed(4);
    // Basic precision for smaller numbers, adjust as needed
    if (price > 0.000001) return price.toPrecision(3);
    return price.toExponential(2); // Very small numbers
};
// --- End Formatting Helper ---

const fetchPrices = async () => {
  if (mints.value.length === 0) return;

  try {
    // Use the correct API endpoint defined in routes/api.php
    const response = await axios.get('/api/tokens/prices', {
      params: {
        // Pass mints as a comma-separated string, as handled by the backend
        mints: mints.value.join(',')
      }
    });
    // Store current prices before updating
    previousPrices.value = { ...prices.value };
    prices.value = response.data;
    // console.log("Fetched prices:", prices.value);
  } catch (error) {
    console.error('Error fetching bulk token prices:', error);
    // Optionally clear prices or handle error state
  }
};

const updateDOMPrices = () => {
    // console.log("Updating DOM with prices:", prices.value);
    for (const mint in prices.value) {
        const priceData = prices.value[mint];
        const prevPriceData = previousPrices.value[mint];

        if (!priceData) continue; // Skip if no data for this mint

        const price = priceData.price_usd;
        const prevPrice = prevPriceData?.price_usd; // Use optional chaining

        // Update Price Span
        const priceElement = document.querySelector(`span.token-price[data-mint="${mint}"]`);
        if (priceElement) {
            if (price !== null) {
                priceElement.innerText = `$${formatPrice(price)}`;

                // Add/Remove classes for visual feedback
                priceElement.classList.remove('text-success', 'text-danger', 'price-updated');
                if (prevPrice !== undefined && price !== prevPrice) {
                    priceElement.classList.add(price > prevPrice ? 'text-success' : 'text-danger');
                    // Optional: Add a temporary class for animation/highlight
                    priceElement.classList.add('price-updated');
                    // Use requestAnimationFrame for smoother removal
                    requestAnimationFrame(() => {
                        setTimeout(() => priceElement.classList.remove('price-updated'), 500);
                    });
                }
            } else {
                 // Handle case where price becomes unavailable
                 priceElement.innerText = 'N/A';
                 priceElement.classList.remove('text-success', 'text-danger', 'price-updated');
            }
        }

        // TODO: Update 24h Change and Volume columns if needed in the future
        // Example for 24h Change (requires adding data-mint and a class to the TD)
        /*
        const changeElement = document.querySelector(`td.token-change[data-mint="${mint}"]`);
        if (changeElement && priceData.change_24h !== null) {
            const change = priceData.change_24h;
            changeElement.innerText = `${change.toFixed(2)}%`;
            changeElement.classList.remove('text-success', 'text-danger');
            if (change !== 0) {
                changeElement.classList.add(change > 0 ? 'text-success' : 'text-danger');
            }
        }
        */
    }
};

// Watch for price changes and update the DOM
watch(prices, updateDOMPrices);

onMounted(() => {
  // Extract mints from the initial prop data
  mints.value = props.initialTokens.map(token => token.mint);
  // console.log("TokenTableUpdater mounted with mints:", mints.value);

  if (mints.value.length > 0) {
    fetchPrices(); // Initial fetch
    intervalId = setInterval(fetchPrices, 15000); // Fetch every 15 seconds
  }
});

onUnmounted(() => {
  if (intervalId) {
    clearInterval(intervalId);
  }
});
</script>

<style scoped>
/* Optional: Style for price update feedback */
.price-updated {
  transition: background-color 0.1s ease-in;
  /* background-color: rgba(255, 255, 0, 0.5); Example highlight */
}
/* Ensure specificity if Bootstrap overrides */
td .text-success, span.text-success {
    color: #198754 !important; /* Bootstrap 5 success color */
}
td .text-danger, span.text-danger {
    color: #dc3545 !important; /* Bootstrap 5 danger color */
}
</style>
